/**
 * WidgetUpdateSurface — the transcript's per-widget wrapper (scoping phase-8
 * Track B / B9). It owns two distinct transitions:
 *
 *   1. Mount cue (all widgets, unchanged from the previous inline wrapper): a
 *      subtle pop + one-shot purple glow when a widget FIRST appears — i.e. a
 *      brand-new card, a card that MOVED to the frontier, or any legacy /
 *      voice-dedup card (each of those arrives under a fresh `widget_id`, so the
 *      transcript's `w:${widget_id}` key changes and this element remounts).
 *
 *   2. In-place update cue (Cloud-materialized capture cards only): a
 *      materialized card (`sc_artifact_card` / `sc_scope_summary_card`) is
 *      re-emitted under a STABLE, engagement-scoped `sc-card:` `widget_id` and
 *      replaced by the 004-p6 `applyWidgetReplace` damping path — same React key,
 *      so this element does NOT remount and the mount cue above never replays.
 *      Without this component that update was an instant hard content swap. Here,
 *      when the card's `data` changes we hold the element mounted and flash the
 *      card's own skeleton IN PLACE for {@link WIDGET_UPDATE_SETTLE_MS} before
 *      revealing the new content, so the card visibly "refreshes" rather than
 *      snapping. Reused skeleton primitives; no hide/show, no remount.
 *
 * Reduced motion: `prefers-reduced-motion` disables the skeleton entirely (an
 * update swaps content instantly — the whole transition is a motion affordance).
 */

import { useEffect, useState, type ComponentType } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { WidgetBlock } from '@shared/conversation-schema';
import { WIDGET_TYPES } from '@shared/widget-schemas';

import { MATERIALIZED_CARD_ID_PREFIX } from '@/hooks/v2/widgetReplace';
import { WidgetRenderer } from './WidgetRenderer';
import { ScArtifactCardSkeleton } from '../conversation/scoping/ScArtifactCard';
import { ScScopeSummaryCardSkeleton } from '../conversation/scoping/ScScopeSummaryCard';

/** How long the in-place skeleton shows while an updated materialized card settles. */
export const WIDGET_UPDATE_SETTLE_MS = 280;

/**
 * Widget-type → matching skeleton for the in-place update transition. Only the
 * Cloud-materialized capture cards (the ones re-emitted under a stable `sc-card:`
 * id via 004-p6 replace-by-widget_id) have an update skeleton; every other widget
 * type just swaps content with no skeleton.
 */
const UPDATE_SKELETONS: Record<string, ComponentType> = {
  [WIDGET_TYPES.SC_ARTIFACT_CARD]: ScArtifactCardSkeleton,
  [WIDGET_TYPES.SC_SCOPE_SUMMARY_CARD]: ScScopeSummaryCardSkeleton,
};

/** Stable fingerprint of the visible payload; a change denotes "the card updated". */
function fingerprintBlock(block: WidgetBlock): string {
  try {
    return JSON.stringify({ d: block.data ?? null, a: block.actions ?? null });
  } catch {
    // Non-serializable data → a constant token so the fingerprint never changes
    // and we simply never skeleton (safe: an update degrades to an instant swap).
    return `!nofp:${block.widget_id}`;
  }
}

interface WidgetUpdateSurfaceProps {
  block: WidgetBlock;
  onAction?: (
    widgetId: string,
    action: string,
    payload?: Record<string, unknown>,
    meta?: { text?: string; label?: string },
  ) => void;
}

export function WidgetUpdateSurface({ block, onAction }: WidgetUpdateSurfaceProps) {
  const prefersReducedMotion = useReducedMotion();

  // Skeleton-on-update is scoped to materialized capture cards — a card carrying
  // the deterministic `sc-card:` id is the ONE set updated in place under a stable
  // React key (004-p6). Anything else keeps only the mount cue.
  const isMaterialized = block.widget_id.startsWith(MATERIALIZED_CARD_ID_PREFIX);
  const UpdateSkeleton = isMaterialized ? UPDATE_SKELETONS[block.widget_type] : undefined;
  const canSkeleton = !!UpdateSkeleton && !prefersReducedMotion;

  const fingerprint = canSkeleton ? fingerprintBlock(block) : null;

  // Track the fingerprint we last committed + whether we're mid-settle. Adjusting
  // this state DURING render (React's "store info from previous renders" pattern),
  // rather than in an effect, swaps to the skeleton in the SAME commit the new
  // data lands — so the new content never flashes for a frame before the skeleton.
  const [settle, setSettle] = useState<{ fingerprint: string | null; active: boolean }>(
    () => ({ fingerprint, active: false }),
  );
  if (canSkeleton && settle.fingerprint !== fingerprint) {
    setSettle({ fingerprint, active: true });
  }

  useEffect(() => {
    if (!settle.active) return;
    const timer = setTimeout(
      () => setSettle((s) => ({ ...s, active: false })),
      WIDGET_UPDATE_SETTLE_MS,
    );
    return () => clearTimeout(timer);
  }, [settle.active, settle.fingerprint]);

  const showSkeleton = canSkeleton && settle.active && !!UpdateSkeleton;

  return (
    <motion.div
      data-testid="widget-update-anim"
      // 004-p6 — scroll anchor a `card_moved` breadcrumb chip scrolls to. Lives on
      // the always-mounted wrapper so it survives the skeleton phase too.
      data-widget-id={block.widget_id}
      // Mount cue only (new / moved / fresh-id card). With a stable materialized
      // key the element doesn't remount, so this never replays on an in-place
      // update — the skeleton below is that update's cue instead. One-shot,
      // motion-safe: framer-motion respects prefers-reduced-motion.
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{
        opacity: 1,
        scale: 1,
        filter: [
          'drop-shadow(0 0 0px rgba(124,58,237,0))',
          'drop-shadow(0 0 7px rgba(124,58,237,0.45))',
          'drop-shadow(0 0 0px rgba(124,58,237,0))',
        ],
      }}
      transition={{ duration: 0.45, ease: 'easeOut' }}
      className="w-full"
    >
      {showSkeleton && UpdateSkeleton ? (
        <div
          data-testid="widget-update-skeleton"
          data-skeleton-for={block.widget_type}
          aria-hidden="true"
        >
          <UpdateSkeleton />
        </div>
      ) : (
        <WidgetRenderer block={block} onAction={onAction} />
      )}
    </motion.div>
  );
}
